function parseExperienceYears(experiences) {
  let totalMonths = 0;

  for (const exp of experiences) {
    if (!exp.startDate) continue;

    const start = new Date(`${exp.startDate}-01`);
    let end;

    if (exp.currentlyWorking) {
      end = new Date();
    } else if (exp.endDate) {
      end = new Date(`${exp.endDate}-01`);
    } else {
      continue;
    }

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) continue;

    const months =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;

    totalMonths += Math.max(months, 0);
  }

  return totalMonths / 12;
}

function getAge(dateOfBirth) {
  if (!dateOfBirth) return null;

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }

  return age;
}

function matchesEducationKeywords(educations, keywords) {
  if (!keywords?.length) return true;

  const haystack = educations
    .flatMap((edu) => [edu.degree, edu.fieldOfStudy, edu.institute, edu.boardUniversity])
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return keywords.some((keyword) => haystack.includes(keyword.toLowerCase()));
}

export function evaluateEligibility(job, formData) {
  const criteria = job.eligibilityCriteria;
  if (!criteria) {
    return { eligible: true, reasons: [], experienceYears: 0, age: null };
  }

  const reasons = [];
  const experienceYears = parseExperienceYears(formData.experiences);
  const age = getAge(formData.dateOfBirth);

  if (criteria.minYearsExperience != null && experienceYears < criteria.minYearsExperience) {
    reasons.push(
      `Minimum ${criteria.minYearsExperience} years of relevant experience required (you have approximately ${experienceYears.toFixed(1)} years).`
    );
  }

  if (criteria.maxYearsExperience != null && experienceYears > criteria.maxYearsExperience) {
    reasons.push(`Maximum ${criteria.maxYearsExperience} years of experience allowed for this role.`);
  }

  if (!matchesEducationKeywords(formData.educations, criteria.educationKeywords)) {
    const required = criteria.educationKeywords.join(', ');
    reasons.push(`Required qualification not met. Expected degrees or fields such as: ${required}.`);
  }

  if (criteria.minAge != null && age != null && age < criteria.minAge) {
    reasons.push(`Minimum age requirement is ${criteria.minAge} years.`);
  }

  if (criteria.maxAge != null && age != null && age > criteria.maxAge) {
    reasons.push(`Maximum age preference for this role is ${criteria.maxAge} years.`);
  }

  if (criteria.requiredCertifications?.length) {
    const certs = formData.educations
      .flatMap((edu) => [edu.degree, edu.fieldOfStudy])
      .join(' ')
      .toLowerCase();

    const missing = criteria.requiredCertifications.filter((cert) => !certs.includes(cert.toLowerCase()));

    if (missing.length) {
      reasons.push(`Missing required certification(s): ${missing.join(', ')}.`);
    }
  }

  return {
    eligible: reasons.length === 0,
    reasons,
    experienceYears,
    age,
  };
}

export function countWords(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Returns true when enough fields are filled to run live eligibility screening. */
export function canEvaluateEligibility(formData) {
  const hasDegree = formData.educations.some(
    (edu) => edu.degree.trim() && edu.fieldOfStudy.trim()
  );

  const hasExperience = formData.experiences.some(
    (exp) => exp.startDate && (exp.currentlyWorking || exp.endDate)
  );

  return Boolean(formData.dateOfBirth && hasDegree && hasExperience);
}

export function isFormComplete(formData) {
  const {
    personal,
    contact,
    educations,
    experiences,
    personalStatement,
    confirmations,
    cvFile,
    minStatementWords,
    maxStatementWords,
  } = formData;

  if (!personal.fullName.trim()) return false;
  if (!personal.fatherName.trim()) return false;
  if (!personal.cnic.trim()) return false;
  if (!personal.dateOfBirth) return false;
  if (!personal.gender) return false;
  if (!personal.maritalStatus) return false;
  if (!personal.nationality) return false;

  if (!contact.mobile.trim()) return false;
  if (!contact.email.trim()) return false;
  if (!contact.currentAddress.trim()) return false;
  if (!contact.permanentAddress.trim()) return false;
  if (!contact.city.trim()) return false;
  if (!contact.province) return false;

  const educationsValid = educations.every(
    (edu) =>
      edu.degree.trim() &&
      edu.fieldOfStudy.trim() &&
      edu.institute.trim() &&
      edu.boardUniversity.trim() &&
      edu.passingYear.trim()
  );
  if (!educationsValid) return false;

  const experiencesValid = experiences.every((exp) => {
    if (!exp.organization.trim() || !exp.designation.trim() || !exp.startDate) return false;
    if (!exp.currentlyWorking && !exp.endDate) return false;
    if (!exp.responsibilities.trim()) return false;
    return true;
  });
  if (!experiencesValid) return false;

  const wordCount = countWords(personalStatement);
  if (wordCount < minStatementWords || wordCount > maxStatementWords) return false;

  if (!confirmations.accurateInfo || !confirmations.meetsRequirements || !confirmations.agreesToPolicies) {
    return false;
  }

  if (!cvFile) return false;

  return canEvaluateEligibility({
    dateOfBirth: personal.dateOfBirth,
    educations,
    experiences,
  });
}
